import {
	getWorkerMissionStatusOrderPosition,
	MISSION_STATUS_COLOR_TO_CSS_VARIABLE,
} from '@/constants/mission';
import { capitalize } from '@/helpers/string';
import i18n from '@/i18n';
import {
	type MissionKanbanStatusInfo,
	type MissionStatusOption,
	WorkerMissionStatus,
	type WorkerMissionStatusInfo,
} from '@/types/mission';

import type { FilterSelectOption } from '@/helpers/filterOptions';
import type { LabelValue } from '@/types/common';

const t = (key: string, named?: Record<string, unknown>): string =>
	i18n.global.t(key, named as Record<string, unknown>) as string;

const tm = (key: string): unknown => i18n.global.tm(key);

type WorkerMissionStatusComparable = {
	status: WorkerMissionStatus;
	acceptedTimestamp?: number;
};

const EMPTY_MISSION_STATUS_INFO: MissionKanbanStatusInfo = {
	statusKanban: '',
	iconColor: MISSION_STATUS_COLOR_TO_CSS_VARIABLE.grey,
	color: 'grey',
	icon: 'hexagon',
};

const EMPTY_WORKER_MISSION_STATUS_INFO: WorkerMissionStatusInfo = {
	label: '',
	color: '',
	icon: 'status-marker',
};

export const getMissionStatusInfoMap = (): Record<
	string,
	Omit<MissionKanbanStatusInfo, 'iconColor'>
> => ({
	to_process: {
		statusKanban: t('page.mission.kanban.toProcess'),
		color: 'yellow',
		icon: 'workflow-2',
	},
	to_confirm: {
		statusKanban: t('page.mission.kanban.toConfirm'),
		color: 'green',
		icon: 'workflow-3',
	},
	validated: {
		statusKanban: t('page.mission.kanban.validated'),
		color: 'blue',
		icon: 'workflow-4',
	},
});

export const getWorkerMissionStatusInfoMap = (): Record<
	string,
	WorkerMissionStatusInfo
> => ({
	placed: {
		label: t('mission.workerStatus.placed.singular'),
		color: 'grey',
		icon: 'status-marker',
	},
	presented: {
		label: t('mission.workerStatus.presented.agency'),
		color: 'yellow',
		icon: 'status-marker',
	},
	validated: {
		label: t('mission.workerStatus.validated.agency'),
		color: 'green',
		icon: 'status-marker',
	},
	proposed: {
		label: t('mission.workerStatus.proposed.agency'),
		color: 'yellow',
		icon: 'status-marker',
	},
	accepted: {
		label: t('mission.workerStatus.accepted.agency'),
		color: 'green',
		icon: 'status-marker',
	},
	declined: {
		label: t('mission.workerStatus.declined.agency'),
		color: 'black',
		icon: 'status-marker',
	},
	confirmed: {
		label: t('mission.workerStatus.confirmed.agency'),
		color: 'blue',
		icon: 'circle-check-filled',
	},
	rejected_by_agency: {
		label: t('mission.workerStatus.rejectedByAgency.agency'),
		color: 'red',
		icon: 'status-marker',
	},
	rejected_by_company: {
		label: t('mission.workerStatus.rejectedByCompany.agency'),
		color: 'red',
		icon: 'status-marker',
	},
	aborted: {
		label: t('mission.workerStatus.aborted.agency'),
		color: 'red',
		icon: 'status-marker',
	},
	not_confirmed: {
		label: t('mission.workerStatus.notconfirmed.agency'),
		color: 'black',
		icon: 'status-marker',
	},
	reproposed: {
		label: t('mission.workerStatus.reproposed.singular'),
		color: 'yellow',
		icon: 'status-marker',
	},
	lost: {
		label: t('mission.workerStatus.lost.agency'),
		color: 'black',
		icon: 'status-marker',
	},
	reproposed_by_sms: {
		label: t('mission.workerStatus.reproposedBySms.singular'),
		color: 'yellow',
		icon: 'status-marker',
	},
});

export const getFallbackInterimStatuses = (): LabelValue[] => [
	{ label: t('interim.status.fallback.imported'), value: 'imported' },
	{ label: t('interim.status.fallback.invited'), value: 'invited' },
	{ label: t('interim.status.fallback.reinvited'), value: 'reinvited' },
	{ label: t('interim.status.fallback.registered'), value: 'registered' },
	{ label: t('interim.status.fallback.inactive'), value: 'inactive' },
	{
		label: t('interim.status.fallback.no_longer_registered'),
		value: 'no_longer_registered',
	},
];

export const compareWorkersByWorkerMissionStatus = (
	workerA: WorkerMissionStatusComparable,
	workerB: WorkerMissionStatusComparable
): number => {
	const workerAStatusPosition = getWorkerMissionStatusOrderPosition(
		workerA.status
	);
	const workerBStatusPosition = getWorkerMissionStatusOrderPosition(
		workerB.status
	);

	if (workerAStatusPosition !== workerBStatusPosition) {
		return workerAStatusPosition - workerBStatusPosition;
	}

	if (
		workerA.status === WorkerMissionStatus.ACCEPTED &&
		workerA.acceptedTimestamp !== undefined &&
		workerB.acceptedTimestamp !== undefined
	) {
		return workerA.acceptedTimestamp - workerB.acceptedTimestamp;
	}

	return 0;
};

export const getMissionStatusInfo = (
	status: string
): MissionKanbanStatusInfo => {
	if (!status) {
		return EMPTY_MISSION_STATUS_INFO;
	}

	const formattedStatus = status.toLowerCase();
	const baseInfo = getMissionStatusInfoMap()[formattedStatus] ?? {
		statusKanban: capitalize(formattedStatus) || formattedStatus,
		color: 'grey',
		icon: 'hexagon',
	};

	return {
		...baseInfo,
		iconColor:
			MISSION_STATUS_COLOR_TO_CSS_VARIABLE[baseInfo.color] ??
			MISSION_STATUS_COLOR_TO_CSS_VARIABLE.grey,
	};
};

export const getWorkerMissionStatusInfo = (
	workerMissionStatus: WorkerMissionStatus
): WorkerMissionStatusInfo => {
	if (!workerMissionStatus) {
		return EMPTY_WORKER_MISSION_STATUS_INFO;
	}

	const formattedStatus = workerMissionStatus.toLowerCase();
	return (
		getWorkerMissionStatusInfoMap()[formattedStatus] ?? {
			label: formattedStatus,
			color: 'grey',
			icon: 'status-marker',
		}
	);
};

export const getMissionAlertOptions = (
	isAgency: boolean
): FilterSelectOption[] => {
	const header = [
		{
			label: t('form.missionAlert'),
			value: '',
			disabled: true,
			selected: true,
		},
		{ label: '-', value: '' },
	];

	if (isAgency) {
		return [
			...header,
			{ label: t('mission.alerts.companyAsk'), value: 'companyAsk' },
			{
				label: t('mission.alerts.companyAskQuote'),
				value: 'companyAskQuote',
			},
			{
				label: t('mission.alerts.workersToConfirmed', {
					total: 'X',
					plural: '(s)',
				}),
				value: 'workersToConfirmed',
			},
			{ label: t('mission.alerts.addWorkers'), value: 'addWorkers' },
			{
				label: t('mission.alerts.missionNotCompleted'),
				value: 'missionNotCompleted',
			},
			{
				label: t('mission.alerts.replaceWorkers', {
					total: 'X',
					plural: '(s)',
				}),
				value: 'replaceWorkers',
			},
			{
				label: t('mission.alerts.missingEvaluation.both'),
				value: 'missingEvaluation',
			},
		];
	}

	return [
		...header,
		{ label: t('mission.alerts.selectAgency'), value: 'selectAgency' },
		{
			label: t('mission.alerts.workersToValidate', {
				total: 'X',
				plural: '(s)',
			}),
			value: 'workersToValidate',
		},
		{
			label: t('mission.alerts.workersToEvaluate', {
				total: 'X',
				plural: '(s)',
			}),
			value: 'workersToEvaluate',
		},
	];
};

export const getInterimStatusOptions = (userContext: string): LabelValue[] => {
	try {
		const workerStatusData = tm('mission.workerStatus');
		if (
			workerStatusData &&
			typeof workerStatusData === 'object' &&
			Object.keys(workerStatusData as object).length > 0
		) {
			return Object.keys(workerStatusData as object).map((key) => ({
				label: t(`mission.workerStatus.${key}.${userContext}`),
				value: key,
			}));
		}
		return getFallbackInterimStatuses();
	} catch {
		return getFallbackInterimStatuses();
	}
};

export const getCancelReasonsOptions = (isAgency: boolean): LabelValue[] => {
	const context = isAgency ? 'agency' : 'company';
	const reasons = tm(`mission.cancelReasons.${context}`) as Record<
		string,
		string
	>;

	return Object.keys(reasons ?? {}).map((key) => ({
		label: `mission.cancelReasons.${context}.${key}`,
		value: key,
	}));
};

export const getFinalMissionStatuses = (
	isAgency: boolean,
	userContext: string
): MissionStatusOption[] => {
	const missionStatus = tm('mission.status') as Record<string, unknown>;
	const statusFilters = isAgency
		? Object.keys(missionStatus ?? {}).filter(
				(status) => status !== 'presented'
			)
		: Object.keys(missionStatus ?? {});

	const labelMap: Record<string, string[]> = {};
	for (const key of statusFilters) {
		const label = t(`mission.status.${key}.${userContext}`);
		if (!labelMap[label]) labelMap[label] = [];
		labelMap[label].push(key);
	}

	return Object.entries(labelMap).map(([label, value]) => ({
		label,
		value: value.length === 1 ? value[0] : value,
	}));
};
